import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
   useCapabilityGrants,
   useCreateCapabilityGrant,
   useRevokeCapabilityGrant,
} from '@/hooks/use-capability-acls';
import { isGrantGatedVisibility } from '@/lib/capability-visibility';

const LOG_PREFIX = '[CapabilityGrantsPanel]';

export interface CapabilityGrantsPanelProps {
   appKey: string;
   capabilityName: string;
   visibility?: string;
   disabled?: boolean;
}

const PAGE_SIZE = 10;

export function CapabilityGrantsPanel({
   appKey,
   capabilityName,
   visibility,
   disabled,
}: CapabilityGrantsPanelProps) {
   const [page, setPage] = useState(1);
   const [email, setEmail] = useState('');
   const listQuery = useCapabilityGrants({
      appKey,
      capabilityName,
      page,
      pageSize: PAGE_SIZE,
   });
   const createMutation = useCreateCapabilityGrant({ appKey, capabilityName });
   const revokeMutation = useRevokeCapabilityGrant({ appKey, capabilityName });

   const isUserScoped = isGrantGatedVisibility(visibility);
   const totalPages = useMemo(() => {
      const total = listQuery.data?.total ?? 0;
      return Math.max(1, Math.ceil(total / PAGE_SIZE));
   }, [listQuery.data?.total]);

   async function handleAdd() {
      const trimmed = email.trim();
      if (!trimmed) return;
      try {
         await createMutation.mutateAsync({ grantee_email: trimmed });
         toast.success('Grant added');
         setEmail('');
      } catch (err) {
         console.error(`${LOG_PREFIX}.handleAdd - failed`, err);
         toast.error('Failed to add grant', {
            description: err instanceof Error ? err.message : 'Unknown error',
         });
      }
   }

   async function handleRevoke(grantID: string) {
      try {
         await revokeMutation.mutateAsync(grantID);
         toast.success('Grant revoked');
      } catch (err) {
         console.error(`${LOG_PREFIX}.handleRevoke - failed`, { grantID, err });
         toast.error('Failed to revoke grant', {
            description: err instanceof Error ? err.message : 'Unknown error',
         });
      }
   }

   const items = listQuery.data?.items ?? [];

   return (
      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-card p-6 shadow-sm dark:border-slate-700">
         <div className="flex flex-col gap-1">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
               User grants
            </h3>
            <p className="text-sm text-muted-foreground">
               {isUserScoped
                  ? 'Users in this list can invoke this user-scoped capability. Broader visibility levels (account, application, organization, global) ignore grants.'
                  : 'Grants are only enforced for capabilities with user visibility. Set visibility to User to restrict access via grants.'}
            </p>
         </div>

         <div className="flex items-end gap-3">
            <div className="flex-1 space-y-2">
               <Label htmlFor="grant-email">Grantee email</Label>
               <Input
                  id="grant-email"
                  value={email}
                  disabled={disabled || createMutation.isPending}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  onKeyDown={(e) => {
                     if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleAdd();
                     }
                  }}
               />
            </div>
            <Button
               type="button"
               variant="default"
               disabled={disabled || !email.trim() || createMutation.isPending}
               onClick={() => void handleAdd()}
            >
               <Plus size={16} className="mr-1" /> Add grant
            </Button>
         </div>

         <div className="rounded-md border border-slate-200 dark:border-slate-700">
            {listQuery.isLoading ? (
               <div className="p-4 text-sm text-muted-foreground">Loading…</div>
            ) : listQuery.isError ? (
               <div className="p-4 text-sm text-destructive">
                  Failed to load grants: {(listQuery.error as Error).message}
               </div>
            ) : items.length === 0 ? (
               <div className="p-4 text-sm text-muted-foreground">No grants yet.</div>
            ) : (
               <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                  {items.map((g) => (
                     <li
                        key={g.id}
                        className="flex items-center justify-between gap-3 px-4 py-3"
                     >
                        <div className="flex min-w-0 flex-col">
                           <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                              {g.grantee_email ?? g.grantee_user_id}
                           </span>
                           <span className="text-xs text-muted-foreground">
                              Granted {new Date(g.granted_at).toLocaleString()}
                              {g.granted_by ? ` by ${g.granted_by}` : ''}
                           </span>
                        </div>
                        <div className="flex items-center gap-2">
                           <Badge
                              variant={g.status === 'active' ? 'default' : 'secondary'}
                              className="text-xs"
                           >
                              {g.status}
                           </Badge>
                           {g.status === 'active' && (
                              <Button
                                 type="button"
                                 variant="ghost"
                                 size="sm"
                                 disabled={disabled || revokeMutation.isPending}
                                 onClick={() => void handleRevoke(g.id)}
                              >
                                 <Trash2 size={14} className="mr-1" /> Revoke
                              </Button>
                           )}
                        </div>
                     </li>
                  ))}
               </ul>
            )}
         </div>

         {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
               <span>
                  Page {page} of {totalPages}
               </span>
               <div className="flex items-center gap-2">
                  <Button
                     type="button"
                     variant="outline"
                     size="sm"
                     disabled={page <= 1}
                     onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                     Previous
                  </Button>
                  <Button
                     type="button"
                     variant="outline"
                     size="sm"
                     disabled={page >= totalPages}
                     onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                     Next
                  </Button>
               </div>
            </div>
         )}
      </div>
   );
}
